import { DataService } from './DataService'

export type RelationType =
  | 'financial_account'
  | 'expense_category'
  | 'revenue_category'
  | 'cost_center'
  | 'person'

export type ListableRelationType = Exclude<RelationType, 'person'>

export interface ContaAzulMapping {
  uuid: string
  relation_type: RelationType
  mappable_type: string
  mappable_uuid: string
  mappable_name: string | null
  ca_uuid: string
  ca_name: string | null
  ca_type: string | null
  active: boolean
}

export interface ContaAzulDefaults {
  financial_account: string | null
  expense_category: string | null
  revenue_category: string | null
  cost_center: string | null
  create_person_when_missing: boolean
  // SC-10765 — Previsão de recebimento no Conta Azul. Desligada (default), a cobrança só vai
  // para lá quando for paga; ligada, o lançamento vai no registro e serve como previsão.
  send_receivable_forecast: boolean
  // SC-10769 — Até quantos meses à frente a cobrança é enviada ao CA. null = sem limite.
  forecast_months_ahead: number | null
  // Preferências de notificação in-app de SUCESSO de sync (por merchant). Default off.
  notify_billing_synced: boolean
  notify_accounts_payable_synced: boolean
  notify_other_revenue_synced: boolean
}

export interface BaseRecord {
  uuid: string
  type: string
  name: string | null
}

export type BaseRecordsByRelation = Record<ListableRelationType, BaseRecord[]>

export interface MappingsIndexResponse {
  error: boolean
  message: string
  defaults: ContaAzulDefaults
  mappings: ContaAzulMapping[]
  base_records: BaseRecordsByRelation
}

export interface MappingsOptionsResponse {
  error: boolean
  relation_type: RelationType
  records: unknown // payload da CA — shape depende do recurso
}

export interface MappingUpsertItem {
  relation_type: RelationType
  mappable_type: string
  mappable_uuid: string
  ca_uuid: string
  ca_name?: string
  ca_type?: string
}

const BASE = '/v1/conta_azul/mappings'
const CONNECTION = '/v1/conta_azul/connection'

export interface ConnectionStatusResponse {
  error: boolean
  connected: boolean
  status: string | null
  connected_at: string | null
  updated_at: string | null
}

export const getConnection = async (): Promise<ConnectionStatusResponse> => {
  const response = await DataService({ url: CONNECTION, type: 'GET' })

  if (response.error) {
    throw new Error(response.message || 'Erro ao consultar a integração.')
  }

  return response.data as ConnectionStatusResponse
}

export const connect = async (
  code: string
): Promise<{ error: boolean; connected: boolean; status: string | null }> => {
  const response = await DataService({
    url: CONNECTION,
    type: 'POST',
    data: { code }
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao conectar à Conta Azul.')
  }

  return response.data
}

export interface BackfillPendingResponse {
  error: boolean
  message?: string
  billings?: { enqueued: number; skipped: number }
  accounts_payables?: { enqueued: number; skipped: number }
  total?: number
  reason?: string
}

export const backfillPending = async (): Promise<BackfillPendingResponse> => {
  const response = await DataService({
    url: `${CONNECTION}/backfill`,
    type: 'POST'
  })

  // No caminho de sucesso, DataService retorna o objeto axios cru e o corpo da
  // API fica em response.data; no caminho de erro, error/message ficam no topo.
  const body = response.data ?? response

  if (response.error && !body.total) {
    throw new Error(
      response.message || 'Erro ao enfileirar o backfill na Conta Azul.'
    )
  }

  return body as BackfillPendingResponse
}

export interface BackfillPreviewResponse {
  error: boolean
  billings_count: number
  accounts_payables_count: number
  total: number
  message?: string
  reason?: string
}

export const backfillPreview = async (): Promise<BackfillPreviewResponse> => {
  const response = await DataService({
    url: `${CONNECTION}/backfill/preview`,
    type: 'GET'
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao consultar os itens pendentes.')
  }

  return response.data as BackfillPreviewResponse
}

export const disconnect = async (): Promise<{
  error: boolean
  connected: boolean
}> => {
  const response = await DataService({ url: CONNECTION, type: 'DELETE' })

  if (response.error) {
    throw new Error(response.message || 'Erro ao desconectar.')
  }

  return response.data
}

export const getMappings = async (): Promise<MappingsIndexResponse> => {
  const response = await DataService({ url: BASE, type: 'GET' })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao listar vínculos da Conta Azul.'
    )
  }

  return response.data as MappingsIndexResponse
}

export const getMappingOptions = async (
  relation_type: RelationType
): Promise<MappingsOptionsResponse> => {
  const response = await DataService({
    url: `${BASE}/options?relation_type=${relation_type}`,
    type: 'GET'
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao buscar opções da Conta Azul.')
  }

  return response.data as MappingsOptionsResponse
}

export interface MappingSuggestion {
  mappable_uuid: string
  ca_uuid: string
  confidence: number
  reason: string
}

export interface MappingSuggestionsResponse {
  error: boolean
  relation_type: ListableRelationType
  suggestions: MappingSuggestion[]
  reason?: string
}

export const getMappingSuggestions = async (
  relation_type: ListableRelationType
): Promise<MappingSuggestionsResponse> => {
  const response = await DataService({
    url: `${BASE}/suggestions?relation_type=${relation_type}`,
    type: 'GET'
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao gerar sugestões.')
  }

  return response.data as MappingSuggestionsResponse
}

export type ImportableRelationType = Extract<
  ListableRelationType,
  'expense_category' | 'revenue_category' | 'cost_center'
>

export interface ImportFromCaResponse {
  error: boolean
  message?: string
  success?: boolean
  reason?: string
  base_record?: { uuid: string; type: string; name: string }
  mapping?: ContaAzulMapping
}

export const importFromCa = async (params: {
  relation_type: ImportableRelationType
  ca_uuid: string
  ca_name: string
  ca_type?: string | null
}): Promise<ImportFromCaResponse> => {
  const response = await DataService({
    url: `${BASE}/import_from_ca`,
    type: 'POST',
    data: params
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao importar item do Conta Azul.')
  }

  return response.data as ImportFromCaResponse
}

export interface ImportFromCaBulkItem {
  relation_type: ImportableRelationType
  ca_uuid: string
  ca_name: string
  ca_type?: string | null
}

export interface ImportFromCaBulkResponse {
  succeeded: Array<{
    ca_uuid: string
    base_record?: { uuid: string; type: string; name: string }
    mapping?: ContaAzulMapping
  }>
  failed: Array<{
    ca_uuid: string
    ca_name: string
    reason: string
    detail?: any
  }>
  total: number
  message?: string
}

export const importFromCaBulk = async (
  items: ImportFromCaBulkItem[]
): Promise<ImportFromCaBulkResponse> => {
  const response = await DataService({
    url: `${BASE}/import_from_ca_bulk`,
    type: 'POST',
    data: { items }
  })

  // No caminho de sucesso (2xx, inclui 207 multi-status), DataService retorna o
  // objeto axios cru, então o corpo da API fica em response.data.
  // No caminho de erro (422 com todos falhando), DataService achata error/message
  // no topo do objeto retornado.
  const body = response.data ?? response

  if (response.error && !body.succeeded) {
    throw new Error(response.message || 'Erro ao importar itens da Conta Azul.')
  }

  return {
    succeeded: body.succeeded || [],
    failed: body.failed || [],
    total: body.total ?? items.length,
    message: body.message
  }
}

export const upsertMappings = async (
  mappings: MappingUpsertItem[]
): Promise<{
  error: boolean
  message: string
  records: ContaAzulMapping[]
}> => {
  const response = await DataService({
    url: BASE,
    type: 'PUT',
    data: { mappings }
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao salvar vínculos.')
  }

  return response.data
}

export const updateDefaults = async (
  defaults: Partial<{
    default_financial_account_ca_uuid: string | null
    default_expense_category_ca_uuid: string | null
    default_revenue_category_ca_uuid: string | null
    default_cost_center_ca_uuid: string | null
    create_person_when_missing: boolean
    send_receivable_forecast: boolean
    forecast_months_ahead: number | null
    notify_billing_synced: boolean
    notify_accounts_payable_synced: boolean
    notify_other_revenue_synced: boolean
  }>
): Promise<{
  error: boolean
  message: string
  defaults: ContaAzulDefaults
}> => {
  const response = await DataService({
    url: `${BASE}/defaults`,
    type: 'PUT',
    data: { defaults }
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao atualizar os padrões.')
  }

  return response.data
}

export const deleteMapping = async (
  uuid: string
): Promise<{ error: boolean; message: string }> => {
  const response = await DataService({
    url: `${BASE}/${uuid}`,
    type: 'DELETE'
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao remover o vínculo.')
  }

  return response.data
}

// RF-10.2 — Sincronizações falhadas com a Conta Azul
export type SyncFailureContaazulableType =
  | 'AccountsPayable'
  | 'OtherRevenue'
  | 'Billing'

export type SyncFailurePartyKind = 'customer' | 'payee' | 'third_party'

export interface SyncFailureParty {
  kind: SyncFailurePartyKind
  uuid: string | null
  name: string | null
  document: string | null
  email: string | null
}

export interface SyncFailure {
  uuid: string
  contaazulable_type: SyncFailureContaazulableType
  contaazulable_uuid: string | null
  description: string | null
  due_date: string | null
  amount: string | number | null
  party: SyncFailureParty | null
  last_error: string | null
  ca_protocol_id: string | null
  ca_installment_id: string | null
  updated_at: string
}

export interface SyncFailuresResponse {
  error: boolean
  message: string
  failures: SyncFailure[]
}

const SYNC_FAILURES = '/v1/conta_azul/sync_failures'

export const getSyncFailures = async (): Promise<SyncFailuresResponse> => {
  const response = await DataService({ url: SYNC_FAILURES, type: 'GET' })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao listar sincronizações falhadas.'
    )
  }

  return response.data as SyncFailuresResponse
}

export const retrySyncFailure = async (
  uuid: string
): Promise<{
  error: boolean
  message: string
  failure?: SyncFailure
}> => {
  const response = await DataService({
    url: `${SYNC_FAILURES}/${uuid}/retry`,
    type: 'POST'
  })

  if (response.error) {
    throw new Error(response.message || 'Erro ao re-enfileirar sincronização.')
  }

  return response.data
}

// Sincroniza (ou re-sincroniza) uma billing específica com a Conta Azul — funciona
// mesmo quando ela ainda não tem conta_azul_info ("Não sincronizado").
export const syncBillingContaAzul = async (
  billingUuid: string
): Promise<{ error: boolean; message: string }> => {
  const response = await DataService({
    url: `/v1/conta_azul/billings/${billingUuid}/sync`,
    type: 'POST'
  })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao sincronizar cobrança com a Conta Azul.'
    )
  }

  return response.data
}

// Sincroniza (ou re-sincroniza) uma conta a pagar específica com a Conta Azul.
export const syncAccountsPayableContaAzul = async (
  accountsPayableUuid: string
): Promise<{ error: boolean; message: string }> => {
  const response = await DataService({
    url: `/v1/conta_azul/accounts_payables/${accountsPayableUuid}/sync`,
    type: 'POST'
  })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao sincronizar conta a pagar com a Conta Azul.'
    )
  }

  return response.data
}

// "Sincronizar novamente" da recorrência inteira: reenvia todos os ciclos (billings) e
// dispara uma única notificação in-app consolidada. `uuid` é o da recorrência.
export const resyncBillingRecurrenceContaAzul = async (
  recurrenceUuid: string
): Promise<{ error: boolean; message: string }> => {
  const response = await DataService({
    url: `/v1/conta_azul/recurrences/${recurrenceUuid}/retry`,
    type: 'POST'
  })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao sincronizar recorrência com a Conta Azul.'
    )
  }

  return response.data
}

// "Sincronizar novamente" da recorrência de contas a pagar: reenvia todos os ciclos
// (accounts_payables) e dispara uma única notificação consolidada. `uuid` é o da recorrência.
export const resyncAccountsPayableRecurrenceContaAzul = async (
  recurrenceUuid: string
): Promise<{ error: boolean; message: string }> => {
  const response = await DataService({
    url: `/v1/conta_azul/accounts_payable_recurrences/${recurrenceUuid}/retry`,
    type: 'POST'
  })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao sincronizar recorrência com a Conta Azul.'
    )
  }

  return response.data
}

export const retryAllSyncFailures = async (): Promise<{
  error: boolean
  message: string
  retried: number
  total: number
  failures: SyncFailure[]
}> => {
  const response = await DataService({
    url: `${SYNC_FAILURES}/retry_all`,
    type: 'POST'
  })

  if (response.error) {
    throw new Error(
      response.message || 'Erro ao re-enfileirar as sincronizações.'
    )
  }

  return response.data
}
